import React, { useState, useMemo } from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "../../useTranslation";
import parse from "html-react-parser";
import { AuthFlowBuilder, AuthFlowParams } from "@monorepo/libraries";
import {
  Typography,
  GridContainer,
  Column,
  Row,
  Button,
  Box,
  BoundedContent
} from "@vp/swan";

import { Screens } from "../ScreensLayout";
import SignInForm from "./SignInForm";
import ResetScreenLink from "../Reset/ResetScreenLink";
import WithSocialProviders from "../Components/withSocialProviders";
import { useNotificationContext } from "../../NotificationContext";
import { Auth0DefaultConfig } from "../../Auth0Context";
import { pushDataToSegment } from "../../segmentData";
import {
  CREATE_ACCOUNT_LABEL,
  CREATE_ACCOUNT_LINK_CLICKED,
  EU_EXPERIMENT_MAP,
  EXPERIMENT_MAP,
  NAVIGATION_CLICKED,
  SIGN_IN_PAGE,
  SITE_VCS
} from "../../constant";
import { useWauth } from "@monorepo/components";

export interface LayoutProps {
  onChangeScreen: (screen: Screens) => void;
  notificationRef?: React.RefObject<HTMLDivElement>;
  isSocialLoginDisabled: (locale: string) => boolean;
  isSignUpLinkHidden: (locale: string) => boolean;
  isAutomaticAgreementEnabled: boolean;
}

const SignIn = (props: LayoutProps) => {
  const [t] = useTranslation("signIn");
  const [resetPageTranslations] = useTranslation("reset");
  const {
    onChangeScreen,
    notificationRef,
    isSocialLoginDisabled,
    isSignUpLinkHidden,
    isAutomaticAgreementEnabled
  } = props;

  const { clearNotification } = useNotificationContext();
  const [showSocialSignIn, setShowSocialSignIn] = useState(true);
  const authParams: AuthFlowParams = new AuthFlowBuilder().build();
  const isSocialLoginDisabledForLocale = isSocialLoginDisabled(
    authParams.culture || ""
  );
  const isSignUpLinkHiddenForLocale = isSignUpLinkHidden(
    authParams.culture || ""
  );

  const [showLoginWithEmail, setShowLoginWithEmail] = useState(
    !!authParams.guestReturnUrl
  );
  const wauth = useWauth();

  const tryJSONParsing = (jsonString: string) => {
    try {
      const parsedCustomText = JSON.parse(jsonString);
      return parsedCustomText;
    } catch (err) {
      console.log("Error occured while parsing customString.", err);
      return null;
    }
  };

  const getPrimaryHeading = () => {
    const { wauth: wauthString } = Auth0DefaultConfig;
    if (wauthString) {
      const wauth = JSON.parse(atob(wauthString));
      const customText = wauth.customText
        ? tryJSONParsing(wauth.customText)
        : null;
      if (
        customText &&
        customText.signIn &&
        customText.signIn.info &&
        customText.signIn.info !== ""
      ) {
        return <>{customText.signIn.info}</>;
      }
      const loginContext: string =
        wauth.loginContext && typeof wauth.loginContext === "string"
          ? wauth.loginContext.toLowerCase()
          : null;
      const skipFasterCheckoutText: boolean = wauth.skipFasterCheckoutText;
      if (skipFasterCheckoutText) {
        return wauth.site === SITE_VCS
          ? t("headings.vcs")
          : t("headings.primary");
      } else if (loginContext) {
        if (t(loginContext) !== loginContext) {
          return <>{t(loginContext)}</>;
        } else {
          console.warn(`Text mapping for "${loginContext}" not found.`);
          return wauth.site === SITE_VCS
            ? t("headings.vcs")
            : t("headings.primary");
        }
      }
      return wauth.site === SITE_VCS
        ? t("headings.vcs")
        : t("headings.primary");
    }
    return wauth.site === SITE_VCS ? t("headings.vcs") : t("headings.primary");
  };
  const getInfo = useMemo(() => {
    if (wauth.site === SITE_VCS) {
      return parse(t("vcsInfo"));
    }
    return parse(t("info"));
  }, [wauth.site, t]);

  const renderTermsAndConditions = () => {
    return (
      <Box mb="to-actions">
        <Typography
          textAlign="left"
          data-testid="signInAgreement"
          fontSkin="footnote"
        >
          <Trans i18nKey={wauth?.site?.toLowerCase() === "vistaprint" && wauth?.culture?.toLowerCase() === "en-in" ? "signIn:termsAndPrivacy.automaticAgreement" : "signIn:termsAndPrivacy.text"}>
            {[
              <a
                data-testid="termsAndConditions-link"
                key="termsAndConditions"
                href={t("termsAndPrivacy.termsOfServicesLink", { culture: wauth?.culture })}
                target="_blank"
                rel="noopener noreferrer"
              >
                termsOfServices
              </a>,
              <a
                data-testid="privacyPolicy-link"
                key="privacyPolicy"
                href={t("termsAndPrivacy.privacyPolicyLink", { culture: wauth?.culture })}
                target="_blank"
                rel="noopener noreferrer"
              >
                privacyPolicy
              </a>
            ]}
          </Trans>
        </Typography>
      </Box>
    );
  };

  React.useEffect(() => {
    if (document.title !== resetPageTranslations("documentTitle")) {
      clearNotification();
    }
    document.title = t("documentTitle");
    pushDataToSegment(SIGN_IN_PAGE, true);
  }, [t]);

  const handleOnClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (
      ((
        EXPERIMENT_MAP.get(((wauth || {}).site || "").toLowerCase()) || []
      ).includes((wauth || {}).culture) ||
        (
          EU_EXPERIMENT_MAP.get(((wauth || {}).site || "").toLowerCase()) || []
        ).includes((wauth || {}).culture)) &&
      isAutomaticAgreementEnabled
    ) {
      pushDataToSegment(NAVIGATION_CLICKED, false, {
        label: CREATE_ACCOUNT_LABEL,
        eventDetail: SIGN_IN_PAGE,
        navigationDetail: CREATE_ACCOUNT_LINK_CLICKED,
        pageName: SIGN_IN_PAGE,
        experiment_name: `${(wauth || {}).culture
          }-randomized-email-preference-opt-in`,
        experiment_value: "true"
      });
    } else {
      pushDataToSegment(NAVIGATION_CLICKED, false, {
        label: CREATE_ACCOUNT_LABEL,
        eventDetail: SIGN_IN_PAGE,
        navigationDetail: CREATE_ACCOUNT_LINK_CLICKED,
        pageName: SIGN_IN_PAGE
      });
    }
    e.preventDefault();
    onChangeScreen(Screens.SignUp);
  };

  const renderResetScreen = () => {
    return <ResetScreenLink onChangeScreen={onChangeScreen} />;
  };

  const getHeadingText = () => {
    return (
      <>
        <Box mb="6">
          <Typography
            textAlign="left"
            fontSkin="title-section"
            data-testid="signInPrimaryHeading"
          >
            <div className="custom-header">
              <h1 >{getPrimaryHeading()}</h1>
              <h2 className="sr-only">Sign in using an account below</h2>
            </div>
          </Typography>
        </Box>
        <Box mb="to-actions">
          <Typography
            textAlign="left"
            fontSkin="body-standard"
            data-testid="signInSecondaryHeading"
          >
            {getInfo}
          </Typography>
        </Box>
      </>
    );
  };
  return (
    <BoundedContent>
      <GridContainer>
        <Row>
          <Column
            span={12}
            offset={0}
            spanSm={8}
            offsetSm={2}
            spanMd={6}
            offsetMd={3}
            spanLg={4}
            offsetLg={4}
            spanXl={4}
            offsetXl={4}
          >
            {getHeadingText()}
          </Column>
        </Row>
        <WithSocialProviders
          setShowSocialSignIn={setShowSocialSignIn}
          showLoginWithEmail={showLoginWithEmail}
          setShowLoginWithEmail={setShowLoginWithEmail}
          showSocialSignIn={showSocialSignIn}
          context="signIn"
          isSocialLoginDisabledForLocale={isSocialLoginDisabledForLocale}
        >
          {!showLoginWithEmail && (
            <>
              {!isSocialLoginDisabledForLocale && (
                <Box mb={4}>
                  <Typography textAlign="left" fontSkin="title-subsection">
                    <div className="custom-header">
                      <h2>{t("heading") }</h2>
                    </div>
                  </Typography>
                </Box>
              )}
              <SignInForm
                notificationRef={notificationRef}
                wauth={wauth}
                renderResetScreen={renderResetScreen}
                setShowSocialSignIn={setShowSocialSignIn}
                setShowLoginWithEmail={setShowLoginWithEmail}
                onChangeScreen={onChangeScreen}
                renderTermsAndConditions={renderTermsAndConditions}
              />
            </>
          )}

          {!wauth.restrictSignup && !isSignUpLinkHiddenForLocale && (
            <>
              <Box my="between-actions">
                <Typography textAlign="center" fontSkin="body-standard-bold">
                  {t("app:or") }
                </Typography>
              </Box>
              <Button
                data-testid="signUpTabTitle"
                id="signup-link"
                skin="secondary"
                width="full-width"
                onClick={handleOnClick}
                aria-label={t("createAccount")}
              >
                {t("createAccount") }
              </Button>
            </>
          )}
        </WithSocialProviders>
      </GridContainer>
    </BoundedContent>
  );
};

export default SignIn;
